import { Attributes, ModelBase, NeoModel } from '@singularsystems/neo-core';
import { injectable } from 'inversify';
import { AppService, Types } from './AppService';
import { InfoPanelView } from '../Views/InformationPanel/AccountInformationVM';
import AppInvestorData from '../Models/Investors/AppInvestorData';
import AppPortfolioData from '../Models/Portfolio/AppPortfolioData';
import LinkedInvestorLookup from "../Models/Investors/LinkedInvestorLookup";

@injectable()
@NeoModel
export default class CurrentInvestorService extends ModelBase {

    constructor() {
        super();
    }

    private _principalInvestorData: AppInvestorData | null = null;

    private _selectedInvestorData: AppInvestorData | null = null;

    @Attributes.Observable(false)
    private _portfolioData: AppPortfolioData | null = null;

    /** The selected investor, or main investor linked to the user if no investor is selected. */
    public get investorData() {
        return this._selectedInvestorData ?? this._principalInvestorData;
    }

    /** Gets the id of the selected investor. */
    public get investorId() {
        return this.investorData?.investorId ?? 0;

    }

    /** Investor linked to the user. May be different to the selected investor */
    public get principalInvestorData() {
        return this._principalInvestorData;
    }

    public get linkedInvestors() {
        if (this.principalInvestorData?.investor) {
            const principalInvestor = new LinkedInvestorLookup();
            principalInvestor.investorID = this.principalInvestorData.investorId;
            principalInvestor.firstName = this.principalInvestorData.investor.firstName;
            principalInvestor.surname = this.principalInvestorData.investor.lastName;

            return [principalInvestor, ...this.principalInvestorData.investor.linkedInvestorList].filter(c => c.investorID !== this.investorData!.investorId);
        }
        return [];
    }

    public initialise(investorId: number | null) {
        if ((this.principalInvestorData?.investorId ?? 0) !== (investorId ?? 0)) {

            if (investorId) {
                this._principalInvestorData = new AppInvestorData(investorId);
            } else {
                this._principalInvestorData = null;
            }
            
            this._selectedInvestorData = null;
        }
    }

    public beginShowAccountInformationPanel(view?: InfoPanelView) {
        if (this.investorData === null) {
            throw Error("Investor is required");
        }
        this.investorData.accountInformationVM.showInfoPanel(view ?? null);
    }

    public toggleAccountInformationPanel() {
        if (this.investorData?.accountInformationVM.panelController.isVisible) {
            this.investorData.accountInformationVM.panelController.beginHideInfoPanel();
        } else {
            this.beginShowAccountInformationPanel();
        }
    }

    public toggleDocumentPanel() {
        if (this.investorData?.documentsVM.panelController.isVisible) {
            this.investorData.documentsVM.panelController.beginHideInfoPanel();
        } else {
            this.investorData?.documentsVM.panelController.showInfoPanel();
        }
    }

    public get portfolioData() {
        const selectedPortfolio = this.investorData?.selectedPortfolio;
        if (selectedPortfolio) {
            if (this._portfolioData?.portfolio.portfolioID !== selectedPortfolio.portfolioID) {
                this._portfolioData = new AppPortfolioData(selectedPortfolio, this.investorData.investorId);
            }
            return this._portfolioData;
        }
        return null;
    }

    public async getPortfolioAsync() {
        await this.investorData!.getInvestorAsync();
        const portfolioData = this.portfolioData;
        if (portfolioData) {
            return await portfolioData.getPortfolioAsync();
        }
        return null;
    }

    public async selectInvestor(linkedInvestorID: number) {
        
        if (linkedInvestorID) {
            const investorData = new AppInvestorData(linkedInvestorID);
            await investorData.fetchInvestor();
    
            this._selectedInvestorData = investorData;
        } else {
            this._selectedInvestorData = null;
        }        

        const portfolioData = this.portfolioData;
        if (portfolioData) {
            await portfolioData.getPortfolioAsync();
        }
    }
}